import { Cross2Icon, DesktopIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Box, Button, Card, Dialog, Flex, Heading, IconButton, Text, TextField } from '@radix-ui/themes';
import { Badge, CountryFlag } from '@digiseg-labs/design-system';
import { ForwardedRef, forwardRef, useState } from 'react';
import { CountryList, CountryStatBadges } from './CountrySelect';
import { CountryState, PlatformState, SearchState, CategoryState, DialogState, AudienceViewMode } from '../../types';
import FilterToggleGroup, { FilterOption } from '../FilterToggleGroup';
import GenericSelect from '../GenericSelect';
import AudienceRequestForm from '../AudienceRequestForm';

type Props = {
  countryState: CountryState;
  platformState: PlatformState;
  searchState: SearchState;
  dialogState: DialogState;
  categoryState: CategoryState;
  onWelcomeDismiss: () => void;
  introSubtitle: string;
  isSearching: boolean;
  audienceViewMode: AudienceViewMode;
  onViewModeChange: (mode: AudienceViewMode) => void;
  hasAudiences: boolean;
};

const AudienceHeroSection = forwardRef<HTMLDivElement, Props>(
  (
    {
      countryState,
      platformState,
      searchState,
      dialogState,
      categoryState,
      introSubtitle,
      onWelcomeDismiss,
      isSearching,
      audienceViewMode,
      onViewModeChange,
      hasAudiences,
    },
    headerSentinelRef: ForwardedRef<HTMLDivElement>,
  ) => {
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [isCustomRequest, setIsCustomRequest] = useState(false);
    const filterOptions: FilterOption[] = [
      { label: 'Interest', value: 'Interest' },
      { label: 'Demographic', value: 'Demographic' },
    ];

    const handleFilterChange = (values: string[]) => {
      // Convert string array back to AudienceViewMode format
      onViewModeChange(values as AudienceViewMode);
    };

    return (
    <>
      <Dialog.Root open={dialogState.showWelcomeDialog} onOpenChange={(open) => { if (!open) onWelcomeDismiss(); }}>
        <Dialog.Content
          className="welcome-dialog"
        >
          <Flex justify="between" align="start" mb="5" gap="4">
            <Box className="welcome-dialog__title">
              <Dialog.Title size="6">Please select a country</Dialog.Title>
            </Box>
            <Dialog.Close aria-label="Close welcome dialog">
              <IconButton
                size="1"
                variant="soft"
                color="gray"
                className="dialog-close-button"
              >
                <Cross2Icon />
              </IconButton>
            </Dialog.Close>
          </Flex>
          <Box className="welcome-dialog__list">
            <CountryList
              countries={countryState.countries || []}
              selectedCountry={countryState.country}
              onCountryChange={countryState.onCountryChange || (() => {})}
            />
          </Box>
          <Flex justify="end" mt="4">
            <Button variant="soft" color="gray" onClick={dialogState.onWelcomeDismiss || (() => {})}>
              Close
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <Box className="w-100">
        <Flex direction="column" gap="3" mb="4">
          <Flex
            direction={{ initial: 'column', md: 'row' }}
            align={{ initial: 'stretch', md: 'center' }}
            justify="between"
            gap="3"
          >
            <Box className="hero-heading">
              <Heading size="7" mb="2">Audience List</Heading>
              <Text color="gray">{introSubtitle}</Text>
            </Box>
            <Flex
              direction="column"
              align={{ initial: 'stretch', md: 'end' }}
              gap="2"
              className="hero-summary-column"
            >
              <Card className="country-summary-card" variant="surface">
                <Flex align="center" justify="between" gap="3" className="country-summary-header">
                  <Flex align="center" gap="2">
                    {countryState.country && (
                      <CountryFlag code={countryState.country.code} size={22} shape="square" />
                    )}
                    <Text size="2" weight="medium" className="text-block">
                      {countryState.country ? countryState.country.display_name : 'No country selected'}
                    </Text>
                  </Flex>
                  <Button
                    variant="soft"
                    color="gray"
                    size="1"
                    onClick={dialogState.onOpenCountrySelector || (() => {})}
                    className="country-change-button"
                  >
                    Change country
                  </Button>
                </Flex>
                {countryState.country ? (
                  <CountryStatBadges stats={countryState.country.reach_stats} />
                ) : (
                  <Text size="1" color="gray">
                    Choose a country to see household, people, and device reach.
                  </Text>
                )}
              </Card>
            </Flex>
          </Flex>
          <Box>
            <Flex
              direction={{ initial: 'column', md: 'row' }}
              align={{ initial: 'stretch', md: 'center' }}
              gap="3"
              wrap="nowrap"
            >
              <Box className="hero-search-column" style={{ flex: '1 1 0%', minWidth: '320px', maxWidth: '50%' }}>
                <TextField.Root
                  size="3"
                  placeholder="Search for an audience…"
                  value={searchState.searchQuery || ''}
                  onChange={(event) => searchState.onSearchChange?.(event.target.value)}
                >
                  <TextField.Slot side="left">
                    <MagnifyingGlassIcon />
                  </TextField.Slot>
                </TextField.Root>
                {(searchState.searchQuery || '').trim().length > 0 && (searchState.searchQuery || '').trim().length < 2 && (
                  <Text size="1" color="gray" mt="2">
                    Keep typing to see matches (minimum 2 characters).
                  </Text>
                )}
              </Box>
              <Flex
                direction={{ initial: 'column', md: 'row' }}
                align={{ initial: 'stretch', md: 'center' }}
                justify="end"
                gap="2"
                wrap="nowrap"
                className="hero-filter-group"
                style={{ flexShrink: 0, minWidth: '260px', marginLeft: 'auto' }}
              >
                <GenericSelect 
                  items={platformState.platforms || []} 
                  selectedItem={platformState.platform} 
                  onItemChange={platformState.onPlatformChange || (() => {})} 
                  withSearch={false} 
                  sectionLabel='DSPs'
                  renderIcon={() => <DesktopIcon />}
                  otherOptions={{enabled: true, position: 'bottom', label: 'Other', value: 'other'}} 
                  emptyOption={{enabled: false}}
                />
              </Flex>
            </Flex>
          </Box>
          {!isSearching && hasAudiences && (
            <Box
              role="group"
              aria-label="Audience view mode switcher"
              style={{ padding: '12px 0' }}
            >
              <Flex align="center" gap="3" justify="center">
                <FilterToggleGroup
                  options={filterOptions}
                  selectedValues={audienceViewMode || []}
                  onSelectionChange={handleFilterChange}
                  visible={true}
                  size="3"
                  style="full"
                  gap="4"
                  justify="center"
                  wrap="wrap"
                />
                <Button
                  variant="classic"
                  color="indigo"
                  size="3"
                  onClick={() => {
                    setIsCustomRequest(true);
                    setShowRequestModal(true);
                  }}
                  style={{ marginLeft: 12 }}
                >
                  Request Custom Audience
                </Button>
              </Flex>
                {categoryState.activeCategory && !isSearching && (
                  <Flex justify="center" mt="2">
                    <Badge
                      size="2"
                      tone="success"
                      appearance="subtle"
                      interactive
                      trailingIcon={<Cross2Icon />}
                      onClick={categoryState.onClearCategory}
                    >
                      Filtering by: {categoryState.activeCategory.display_name}
                    </Badge>
                  </Flex>
                )}
                <Dialog.Root open={showRequestModal} onOpenChange={(open) => {
                  setShowRequestModal(open);
                  if (!open) setIsCustomRequest(false);
                }}>
                  <Dialog.Content style={{ maxWidth: 600 }}>
                    <AudienceRequestForm
                      onBack={() => {
                        setShowRequestModal(false);
                        setIsCustomRequest(false);
                      }}
                      onClose={() => {
                        setShowRequestModal(false);
                        setIsCustomRequest(false);
                      }}
                      onPlatformSelected={platformState.onPlatformChange || (() => {})}
                      platforms={platformState.platforms || []}
                      countrySelected={countryState.country?.display_name || ''}
                      selectedPlatform={platformState.platform}
                      requestType="classic"
                      isCustomRequest={isCustomRequest}
                    />
                  </Dialog.Content>
                </Dialog.Root>
            </Box>
          )}
        </Flex>
      </Box>
      <Box
        ref={headerSentinelRef}
        aria-hidden="true"
        className="header-sentinel"
      />
    </>
    );
  }
);

AudienceHeroSection.displayName = 'AudienceHeroSection';

export default AudienceHeroSection;
